import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProductTable = ({ products, onView, onEdit, onDelete }) => {
  const getStatusColor = (status) => {
    const colors = {
      'In Stock': 'bg-success/10 text-success border-success/20',
      'Low Stock': 'bg-warning/10 text-warning border-warning/20',
      'Out of Stock': 'bg-error/10 text-error border-error/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground border-border';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    })?.format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getTrackableBadge = (isTrackable) => {
    if (isTrackable) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary border border-primary/20">
          Trackable
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground border border-border">
        Non-Trackable
      </span>
    );
  };

  if (products?.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border shadow-elevation-1 p-12 text-center">
        <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Package" size={32} color="var(--color-muted-foreground)" />
        </div>
        <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
          No products found
        </h3>
        <p className="text-muted-foreground">
          Try adjusting your search or filters to find what you're looking for.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Product</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">SKU</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Category</th>
              <th className="text-center px-4 py-3 text-sm font-semibold text-foreground">Unit</th>
              <th className="text-right px-4 py-3 text-sm font-semibold text-foreground">Price</th>
              <th className="text-center px-4 py-3 text-sm font-semibold text-foreground">Quantity</th>
              <th className="text-center px-4 py-3 text-sm font-semibold text-foreground">Status</th>
              <th className="text-center px-4 py-3 text-sm font-semibold text-foreground">Tracking</th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-foreground">Supplier</th>
              <th className="text-right px-4 py-3 text-sm font-semibold text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {products?.map((product) => (
              <tr key={product?.id} className="hover:bg-muted/20 transition-smooth">
                <td className="px-4 py-4">
                  <div>
                    <p className="font-medium text-foreground">{product?.name}</p>
                    <p className="text-sm text-muted-foreground truncate max-w-xs">
                      {product?.description}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm font-mono text-muted-foreground">{product?.sku}</span>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-foreground">{product?.category}</span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-sm font-medium text-foreground">{product?.unit}</span>
                </td>
                <td className="px-4 py-4 text-right">
                  <span className="text-sm font-semibold text-foreground">
                    {formatCurrency(product?.price)}
                  </span>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-sm text-foreground">
                    {product?.isTrackable ? product?.quantity : 'N/A'}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex justify-center">
                    <span
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusColor(
                        product?.status
                      )}`}
                    >
                      {product?.status}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex justify-center">
                    {getTrackableBadge(product?.isTrackable)}
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className="text-sm text-muted-foreground">{product?.supplier}</span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onView(product)}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onEdit(product)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => {
                        if (window.confirm(`Are you sure you want to delete ${product?.name}?`)) {
                          onDelete(product?.id);
                        }
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductTable;